import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { Loader2, Bell } from 'lucide-react';

export default function AuthModal({ open, onClose }) {
  const [tab, setTab] = useState('login');
  const { login, registro } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({ email: '', password: '', nombre: '', telefono: '', marketing_push_enabled: true });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 'login') {
        await login(form.email, form.password);
      } else {
        await registro(form);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al procesar la solicitud.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            {tab === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 p-1 bg-zinc-800/50 rounded-full mb-4">
          {['login', 'registro'].map(t => (
            <button
              key={t}
              data-testid={`auth-tab-${t}`}
              onClick={() => { setTab(t); setError(''); }}
              className={`flex-1 py-2 text-sm font-medium rounded-full transition-all ${tab === t ? 'bg-[#E84C1C] text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              {t === 'login' ? 'Ingresar' : 'Registrarse'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {tab === 'registro' && (
            <>
              <div>
                <Label className="text-zinc-400 text-xs">Nombre completo *</Label>
                <Input data-testid="auth-nombre" value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Tu nombre" required className="bg-zinc-950 border-zinc-800 h-11 mt-1" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Teléfono <span className="text-[#E84C1C]">*</span></Label>
                <Input data-testid="auth-telefono" value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="+595 9XX XXX XXX" required className="bg-zinc-950 border-zinc-800 h-11 mt-1" />
              </div>
            </>
          )}
          <div>
            <Label className="text-zinc-400 text-xs">Email</Label>
            <Input data-testid="auth-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="tu@email.com" required className="bg-zinc-950 border-zinc-800 h-11 mt-1" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Contraseña</Label>
            <Input data-testid="auth-password" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min. 6 caracteres" required minLength={6} className="bg-zinc-950 border-zinc-800 h-11 mt-1" />
          </div>

          {tab === 'registro' && (
            <div
              data-testid="auth-push-toggle"
              onClick={() => set('marketing_push_enabled', !form.marketing_push_enabled)}
              className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/40 border border-zinc-800/50 cursor-pointer hover:bg-zinc-800/60 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-[#E84C1C]/10 flex items-center justify-center shrink-0">
                <Bell size={16} className="text-[#E84C1C]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium">Recibir promos y novedades</p>
                <p className="text-[10px] text-zinc-500">Notificaciones de ofertas del día</p>
              </div>
              <div className={`w-9 h-5 rounded-full transition-colors relative ${form.marketing_push_enabled ? 'bg-[#E84C1C]' : 'bg-zinc-700'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${form.marketing_push_enabled ? 'left-[18px]' : 'left-0.5'}`} />
              </div>
            </div>
          )}

          {error && <p data-testid="auth-error" className="text-red-400 text-sm">{error}</p>}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#E84C1C] text-white font-medium py-3 rounded-full hover:bg-[#d43f12] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {tab === 'login' ? 'Ingresar' : 'Crear cuenta'}
          </button>

          {tab === 'login' && (
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => { onClose(); navigate('/forgot-password'); }}
              className="w-full text-center text-xs text-zinc-500 hover:text-[#E84C1C] transition-colors"
            >
              ¿Olvidaste tu contraseña?
            </button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
